import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { Button, Modal } from 'antd';
import { InfoCircleOutlined } from "@ant-design/icons";

import { colors } from '../../utils/variables.styles';

import EditableSection from '../../components/EditableSection';
import SelectEdit from '../../components/SelectEdit';
import RangeEdit from "../../components/RangeEdit/RangeEdit";

import selector from './doorsEditor.selector';

import { infoShape } from '../../types/info.types';
import { availableType } from '../../types/available.types';

import { setDoors, setSelected as setSelectedParameter } from '../Construction/construction.reducer';
import withTranslation from "../../components/WithTranslation/WithTranslation";
import { selectDoors, selectWidth, selectLength } from "../Construction/construction.reducer";

const Container = styled.div`
  background: ${colors.whiteer};
  padding: 10px 24px;
  margin-left: -27px;
`;

const Spacer = styled.div`
  padding: 10px 24px;
  font-weight: 600;
  max-width: 280px;
`;

const ImgWrapper = styled.img`
  max-width: 100%;
  display: block;
  margin: 15px auto;
`;

const ButtonWrapper = styled.div`
  padding-top: 5px;
`;

const ConstructionExtras = ({ collapsed, t }) => {
  const dispatch = useDispatch();

  // 🔹 wszystko z selektora
  const {
    loaded,
    door_back_count_available,
    door_back_count_info,
    door_back_count_selected,
    door_back_size_available,
    door_back_size_info,
    door_back_size_selected,
    door_front_count_available,
    door_front_count_info,
    door_front_count_selected,
    door_front_size_available,
    door_front_size_info,
    door_front_size_selected,
    door_left_count_available,
    door_left_count_info,
    door_left_count_selected,
    door_left_size_available,
    door_left_size_info,
    door_left_size_selected,
    door_right_count_available,
    door_right_count_info,
    door_right_count_selected,
    door_right_size_available,
    door_right_size_info,
    door_right_size_selected,
  } = useSelector(selector);

  const width = useSelector(selectWidth);
  const length = useSelector(selectLength);
  const doors = useSelector(selectDoors);

  const doorsWidth = 100;

  const openModal = () => {
    Modal.info({
      title: door_front_size_info?.info_title,
      content: (
        <>
          {door_front_size_info?.img_one && <ImgWrapper src={door_front_size_info.img_one} />}
          {door_front_size_info?.img_two && <ImgWrapper src={door_front_size_info.img_two} />}
          {door_front_size_info?.info_content}
          {door_front_size_info?.img_three && <ImgWrapper src={door_front_size_info.img_three} />}
          {door_front_size_info?.img_four && <ImgWrapper src={door_front_size_info.img_four} />}
        </>
      )
    });
  };

  const handleDoorCountChange = (wall, paramKey, count) => {
    let doorsCount = 0;
    if (count === 15) doorsCount = 1;
    if (count === 16) doorsCount = 2;

    if (wall === 'front_wall' && count === 10) doorsCount = 2;
    if (wall === 'front_wall' && count === 9) doorsCount = 1;
    if (wall === 'front_wall' && count === 8) doorsCount = 0;

    const updatedDoors = Array.from({ length: doorsCount }, (_, index) => {
      const existingDoor = doors[wall]?.doors?.[index];
      return existingDoor || {
        id: index,
        position: 20,
        size: null
      };
    });

    dispatch(setDoors(wall, updatedDoors));


    dispatch(setSelectedParameter(paramKey, count));
  };

  const onDoorPositionChange = (wall, index, value) => {
    const updatedDoorsArray = doors[wall].doors.map((d, i) =>
      i === index ? { ...d, position: value } : d
    );

    dispatch(setDoors(wall, updatedDoorsArray));
  };

  const renderWallControls = (wall, countSelected, countAvailable, countInfo, sizeSelected, sizeAvailable, sizeInfo, countKey, sizeKey, max) => (
    <>
      <SelectEdit
        selected={countSelected}
        loaded={loaded}
        title={countInfo.name}
        onChange={(count) => handleDoorCountChange(wall, countKey, count)}
        options={countAvailable.map((el) => ({ id: el.id, value: `${el.value}` }))}
        nested
      />
      <SelectEdit
        selected={sizeSelected}
        loaded={loaded}
        title={sizeInfo.name}
        onChange={(id) => dispatch(setSelectedParameter(sizeKey, id))}
        options={sizeAvailable.map((el) => ({ id: el.id, value: `${el.value}` }))}
        nested
      />
      {doors[wall]?.doors?.map((door, index) => (
        <RangeEdit
          key={door.id}
          title={
            doors[wall].doors.length === 1
              ? t(`door_${wall.split('_')[0]}_position`)
              : `${t(`door_${wall.split('_')[0]}_position`)} ${index + 1}`
          }
          min={20}
          step={5}
          max={max}
          unit={'cm'}
          selected={door.position}
          onChange={(value) => onDoorPositionChange(wall, index, value)}
        />
      ))}
    </>
  );

  return (
    <>
      {loaded && (
        <EditableSection
          collapsed={collapsed}
          title={t('doors')}
          nested
          subsection
          open={false}
        >
          <Spacer>{t('door_location')}</Spacer>

          {renderWallControls(
            'back_wall',
            door_back_count_selected,
            door_back_count_available,
            door_back_count_info,
            door_back_size_selected,
            door_back_size_available,
            door_back_size_info,
            'door_back_count',
            'door_back_size',
            width - 20 - doorsWidth
          )}

          {renderWallControls(
            'front_wall',
            door_front_count_selected,
            door_front_count_available,
            door_front_count_info,
            door_front_size_selected,
            door_front_size_available,
            door_front_size_info,
            'door_front_count',
            'door_front_size',
            width - 20 - doorsWidth
          )}

          {renderWallControls(
            'left_wall',
            door_left_count_selected,
            door_left_count_available,
            door_left_count_info,
            door_left_size_selected,
            door_left_size_available,
            door_left_size_info,
            'door_left_count',
            'door_left_size',
            length - 20 - doorsWidth
          )}

          {renderWallControls(
            'right_wall',
            door_right_count_selected,
            door_right_count_available,
            door_right_count_info,
            door_right_size_selected,
            door_right_size_available,
            door_right_size_info,
            'door_right_count',
            'door_right_size',
            length - 20 - doorsWidth
          )}

          <Container>
            <ButtonWrapper>
              <Button
                icon={<InfoCircleOutlined />}
                type="primary"
                block
                onClick={openModal}
              >
                {t('more_information')}
              </Button>
            </ButtonWrapper>
          </Container>
        </EditableSection>
      )}
    </>
  );
};

ConstructionExtras.propTypes = {
  collapsed: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
  door_back_count_available: PropTypes.arrayOf(availableType),
  door_back_count_info: infoShape,
  door_back_count_selected: PropTypes.number,
  door_back_size_available: PropTypes.arrayOf(availableType),
  door_back_size_info: infoShape,
  door_back_size_selected: PropTypes.number,
  door_front_count_available: PropTypes.arrayOf(availableType),
  door_front_count_info: infoShape,
  door_front_count_selected: PropTypes.number,
  door_front_size_available: PropTypes.arrayOf(availableType),
  door_front_size_info: infoShape,
  door_front_size_selected: PropTypes.number,
  door_left_count_available: PropTypes.arrayOf(availableType),
  door_left_count_info: infoShape,
  door_left_count_selected: PropTypes.number,
  door_left_size_available: PropTypes.arrayOf(availableType),
  door_left_size_info: infoShape,
  door_left_size_selected: PropTypes.number,
  door_right_count_available: PropTypes.arrayOf(availableType),
  door_right_count_info: infoShape,
  door_right_count_selected: PropTypes.number,
  door_right_size_available: PropTypes.arrayOf(availableType),
  door_right_size_info: infoShape,
  door_right_size_selected: PropTypes.number,
  loaded: PropTypes.bool
};

export default withTranslation(ConstructionExtras);
